import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Property } from "@/types/property";
import { PropertyContract } from "@/hooks/useContracts";
import { Organization } from "@/hooks/useOrganizations";
import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { filterActiveContracts } from "@/utils/contractUtils";

interface DashboardMobileFiltersProps {
  handlePropertyChange: (value: string) => void;
  monthOptions: { value: string; label: string }[];
  properties: Property[];
  organizations: Organization[];
  contracts: PropertyContract[];
  selectedMonth: string;
  selectedProperties: string[];
  selectedOrganizations: string[];
  selectedYear: string;
  setSelectedMonth: React.Dispatch<React.SetStateAction<string>>;
  setSelectedYear: React.Dispatch<React.SetStateAction<string>>;
  setSelectedOrganizations: React.Dispatch<React.SetStateAction<string[]>>;
  yearOptions: number[];
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
}

const DashboardMobileFilters = ({
  handlePropertyChange,
  monthOptions,
  properties,
  organizations,
  contracts,
  selectedMonth,
  selectedProperties,
  selectedOrganizations,
  selectedYear,
  setSelectedMonth,
  setSelectedYear,
  setSelectedOrganizations,
  yearOptions,
  goToPreviousMonth,
  goToNextMonth,
  canGoToPreviousMonth,
  canGoToNextMonth,
}: DashboardMobileFiltersProps) => {
  
  // Filtrar propiedades basadas en organizaciones seleccionadas (cascada)
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      const activePropertyIds = new Set(
        filterActiveContracts(contracts || []).map(c => c.property_id)
      );
      return properties.filter(p => activePropertyIds.has(p.id));
    }
    
    const activeContracts = filterActiveContracts(contracts || []);
    const propertyIdsInOrgs = activeContracts
      .filter(c => selectedOrganizations.includes(c.organization_id || ''))
      .map(c => c.property_id);
    
    return properties.filter(p => propertyIdsInOrgs.includes(p.id));
  }, [properties, contracts, selectedOrganizations]);
  
  const handleOrganizationChange = (value: string) => {
    if (value === "all") {
      setSelectedOrganizations([]);
    } else {
      setSelectedOrganizations([value]);
    }
  };

  return (
    <>
      <div className="flex gap-2 w-full items-center">
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-20 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {yearOptions.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={goToPreviousMonth}
          disabled={!canGoToPreviousMonth}
          className="h-8 w-8 rounded-full shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="flex-1 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue />
          </SelectTrigger>

          <SelectContent>
            {monthOptions.map((month) => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="ghost"
          size="icon"
          onClick={goToNextMonth}
          disabled={!canGoToNextMonth}
          className="h-8 w-8 rounded-full shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="w-full">
        <Select
          value={
            selectedOrganizations.length === 0 ? "all" : selectedOrganizations[0]
          }
          onValueChange={handleOrganizationChange}
        >
          <SelectTrigger className="w-full rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue placeholder="Seleccionar organización" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las organizaciones</SelectItem>
            {organizations?.map((org) => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="w-full">
        <Select
          value={
            selectedProperties.length === 0 ? "all" : selectedProperties[0]
          }
          onValueChange={handlePropertyChange}
        >
          <SelectTrigger className="w-full rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 text-foreground">
            <SelectValue placeholder="Seleccionar propiedad" />
          </SelectTrigger>

          <SelectContent>
            <SelectItem value="all">Todas las propiedades</SelectItem>
            {filteredProperties?.map((property) => (
              <SelectItem key={property.id} value={property.id}>
                {property.shortName || property.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </>
  );
};

export default DashboardMobileFilters;
